
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { 
  ChevronRight, 
  Shield, 
  CheckCircle2,
  Globe,
  Server,
  FileCheck,
  BarChart3,
  Users,
  Clock,
  Lock,
  Network,
  Search,
  Settings,
  Scale,
  Headset
} from "lucide-react";

import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Helmet } from "react-helmet-async";

const EnterprisePage = () => {
  const { t } = useTranslation("common");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true });
  
  useEffect(() => {
    document.title = "Enterprise Solutions | Verdea";
    
    if (featuresInView) {
      controls.start("visible");
    }
  }, [controls, featuresInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const enterpriseFeatures = [
    {
      icon: Headset,
      title: "Dedicated Support",
      description: "24/7 priority support with a dedicated customer success manager to guide your CSRD compliance journey."
    },
    {
      icon: Settings,
      title: "Custom Integration",
      description: "Seamless integration with your existing ERP, financial, and sustainability systems through secure APIs."
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Deep insights and custom reporting capabilities with advanced AI-powered analytics for decision making."
    },
    {
      icon: Scale,
      title: "Multi-Entity Management",
      description: "Manage CSRD reporting across multiple subsidiaries, business units, and geographic locations from a single platform."
    },
    {
      icon: Lock,
      title: "Enhanced Security",
      description: "Enterprise-grade security with single sign-on, role-based access controls, and audit logging for compliance."
    },
    {
      icon: Users,
      title: "Unlimited Users",
      description: "Include everyone in your sustainability reporting process with unlimited user accounts and customizable permissions."
    }
  ];

  const enterprisePackages = [
    {
      name: "Enterprise Essentials",
      price: "Contact for pricing",
      description: "For large companies requiring compliant CSRD reporting",
      features: [
        "Dedicated account manager",
        "Custom onboarding program",
        "API integration options",
        "Single sign-on (SSO)",
        "Multi-entity management",
        "Enhanced security features",
        "Advanced analytics dashboard"
      ]
    },
    {
      name: "Enterprise Premium",
      price: "Contact for pricing",
      description: "Complete solution for complex multinational organizations",
      features: [
        "All Enterprise Essentials features",
        "24/7 priority support",
        "Customized AI model training",
        "Custom report templates",
        "Unlimited document processing",
        "Private cloud deployment option",
        "Quarterly compliance reviews",
        "Dedicated implementation team"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Enterprise Solutions | Verdea</title>
        <meta name="description" content="Verdea's enterprise-grade CSRD compliance solutions with enhanced security, custom integrations, and dedicated support for large organizations" />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
                Enterprise Solutions
              </h1>
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Advanced CSRD compliance solutions designed for large organizations with complex reporting requirements and enhanced security needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-demo">
                  <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                    Schedule a Demo
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative hidden lg:block"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-200/30 to-emerald-300/10 rounded-xl transform rotate-3" />
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="/screens/aifguard_dashboard.png"
                  alt="Verdea Enterprise Dashboard"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise Plans Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Enterprise Packages
            </h2>
            <p className="text-lg text-slate-600">
              Tailored solutions to meet the needs of your organization's size and complexity
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {enterprisePackages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100 relative overflow-hidden"
              >
                {index === 1 && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg transform rotate-0 translate-x-0">
                      MOST POPULAR
                    </div>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2 text-slate-900">{pkg.name}</h3>
                <p className="text-emerald-700 font-semibold mb-4">{pkg.price}</p>
                <p className="text-slate-600 mb-6">{pkg.description}</p>
                <div className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="flex items-start">
                      <div className="bg-emerald-50 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact">
                  <Button className={`w-full ${index === 1 ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-white border-emerald-700 border text-emerald-700 hover:bg-emerald-50'}`}>
                    Contact Sales
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Tabs Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Enterprise Benefits
            </h2>
            <p className="text-lg text-slate-600">
              Designed for the specific needs of large organizations
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Apple-style Tabs */}
            <Tabs defaultValue="security" className="w-full">
              {/* Apple-inspired Tab Navigation */}
              <div className="relative flex justify-center mb-16">
                <div className="absolute bottom-0 w-full border-b border-gray-200"></div>
                <TabsList className="relative bg-transparent border-0 flex space-x-8 p-0 z-10">
                  {[
                    { value: "security", label: "Security" },
                    { value: "integration", label: "Integration" },
                    { value: "support", label: "Support" }
                  ].map((tab) => (
                    <TabsTrigger 
                      key={tab.value}
                      value={tab.value}
                      className="px-1 py-2 text-base font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 transition-all duration-200 bg-transparent hover:text-emerald-600 rounded-none focus-visible:outline-none focus-visible:ring-0"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              
              {/* Tab Content with Apple-like Styling */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-0"
              >
                <TabsContent value="security" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Enterprise-Grade Security</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">
                          Our enterprise solution includes advanced security features designed for organizations with strict compliance requirements.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Single Sign-On (SSO) integration",
                            "Role-based access controls",
                            "End-to-end encryption for all data",
                            "Regular penetration testing",
                            "Detailed audit logging for compliance",
                            "Data residency options for regulatory compliance"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <Shield className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/login_screen.png" 
                          alt="Enterprise Security" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="integration" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Seamless Integration</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">
                          Connect Verdea with your existing systems and workflows through our robust API and integration options.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "REST API for custom integrations",
                            "ERP system connectors (SAP, Oracle, etc.)",
                            "Financial reporting system integration",
                            "ESG data platform connectors",
                            "Custom data pipeline development",
                            "Automated data import and validation"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <Network className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/dashboard_overview.png" 
                          alt="Enterprise Integration" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="support" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">Premium Support</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">
                          Enterprise clients receive dedicated support and services to ensure successful implementation and ongoing compliance.
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Dedicated account manager",
                            "24/7 priority support",
                            "Custom onboarding program",
                            "Regular compliance review meetings",
                            "Implementation assistance",
                            "Training for all team members"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <Headset className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/dashboard_overview.png"
                          alt="Enterprise Support" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
              </motion.div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Enterprise Features Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Enterprise Features
            </h2>
            <p className="text-lg text-slate-600">
              Advanced capabilities designed for large organizations with complex sustainability reporting needs
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {enterpriseFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
                >
                  <div className="mb-4 p-3 bg-emerald-50 w-fit rounded-lg">
                    <Icon className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Contact our enterprise sales team to discuss your organization's specific needs and how Verdea can help you achieve CSRD compliance.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/book-demo">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]">
                  Schedule a Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-600 min-w-[180px]">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnterprisePage;
